import * as React from "react";
import type { Ordering } from "@/types/base";
import type { ColumnFilters, DateFilter } from "@/modules/snapshots/grid/sortAndFilter";
import {
  buildSnapshotGridQueryPayload,
  type SnapshotGridQueryPayload,
} from "@/modules/snapshots/grid/gridQueryPayload";
import {
  getHasNextSnapshotGridPage,
  mergeSnapshotGridRows,
  replaceSnapshotGridRows,
} from "@/modules/snapshots/grid/gridInfiniteRows";
import {
  useRefreshedSnapshotGridRows,
  useSnapshotGridDataSource,
  useSnapshotGridRowFetch,
  shouldRefetchSnapshotGrid,
  type SnapshotGridCapabilities,
  type SnapshotGridDataSourcePage,
} from "@/modules/snapshots/grid/dataSource";
import { useSnapshotGridConfiguration } from "@/modules/snapshots/grid/useSnapshotGridConfiguration";
import { useSnapshotCollectionUpdates } from "@/modules/snapshots/grid/useSnapshotCollectionUpdates";
import { adaptSnapshotOutcomeRow, type SnapshotGridRowModel } from "@/modules/snapshots/grid/rowModel";
import type { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import type { SnapshotViewConfiguration } from "@/modules/snapshots/snapshotViews.types";
import type { CollectionProps } from "@/modules/snapshots/grid/types";
import { PAGE_SIZE } from "@/modules/snapshots/grid/constants";

export const getSnapshotGridViewKey = (collectionId: string | undefined, payload: SnapshotGridQueryPayload): string =>
  JSON.stringify({
    collectionId: collectionId ?? null,
    query: payload.query,
    subcollectionId: payload.subcollection_id ?? null,
    ordering: payload.ordering,
    columnFilters: payload.column_filters,
  });

/**
 * The 1-based page cursor of the infinite grid. It lives outside `useSnapshotGridRows` because every view
 * change (search, ordering, filters, dates) must reset it synchronously, in the same batch as the change;
 * otherwise one render would request the old offset with the new filters.
 * `onReset` must be referentially stable: the route-reset effect in `useSnapshotGridRows` depends on it.
 */
export const useSnapshotGridPageCursor = (onReset: () => void) => {
  const [page, setPage] = React.useState(1);
  const isGridPageRequestPendingRef = React.useRef(false);

  const resetGridPageForViewChange = React.useCallback(() => {
    setPage(1);
    onReset();
    isGridPageRequestPendingRef.current = false;
  }, [onReset]);

  return { page, setPage, isGridPageRequestPendingRef, resetGridPageForViewChange };
};

export type SnapshotGridPageCursor = ReturnType<typeof useSnapshotGridPageCursor>;

interface Input {
  cursor: SnapshotGridPageCursor;
  mode: CollectionProps["mode"];
  collectionId: string;
  snapshotViewId: string | undefined;
  capabilities: SnapshotGridCapabilities;
  refreshSignal: number | undefined;
  onQueryChange: ((query: SnapshotGridQueryPayload) => void) | undefined;
  isContractFamilyProjection: boolean;
  subcollectionId: string | undefined;
  contractFamilyId: string | undefined;
  supplierContractFolderId: string | undefined;
  query: string;
  ordering: Ordering;
  columnFilters: ColumnFilters;
  dateFilter: DateFilter;
  initialConfiguration: SnapshotViewConfiguration | undefined;
  initialConfigurationAppliedRef: React.MutableRefObject<boolean>;
  hiddenColumns: string[];
  columnOrder: string[];
  noteColumnOrder: string[];
  dateRangeTouched: boolean;
  stableColumnOrder: string[] | undefined;
}

/**
 * Loads the collection grid page by page, accumulating rows across pages until the route or the view
 * (search, ordering, filters, dates) changes.
 */
export const useSnapshotGridRows = ({
  cursor,
  mode,
  collectionId,
  snapshotViewId,
  capabilities,
  refreshSignal,
  onQueryChange,
  isContractFamilyProjection,
  subcollectionId,
  contractFamilyId,
  supplierContractFolderId,
  query,
  ordering,
  columnFilters,
  dateFilter,
  initialConfiguration,
  initialConfigurationAppliedRef,
  hiddenColumns,
  columnOrder,
  noteColumnOrder,
  dateRangeTouched,
  stableColumnOrder,
}: Input) => {
  const { page, setPage, isGridPageRequestPendingRef, resetGridPageForViewChange } = cursor;
  const [loadedGridRows, setLoadedGridRows] = React.useState<SnapshotGridRowModel[]>([]);
  const [gridMetadata, setGridMetadata] = React.useState<SnapshotGridDataSourcePage | null>(null);
  const [appliedGridViewKey, setAppliedGridViewKey] = React.useState<string | null>(null);

  // Must stay declared before the page-accumulation effect below: a route change clears the rows first.
  React.useEffect(() => {
    resetGridPageForViewChange();
    setLoadedGridRows([]);
    setGridMetadata(null);
    setAppliedGridViewKey(null);
  }, [collectionId, subcollectionId, contractFamilyId, supplierContractFolderId, resetGridPageForViewChange]);

  React.useEffect(() => {
    resetGridPageForViewChange();
  }, [query, ordering, columnFilters, dateFilter, resetGridPageForViewChange]);

  const gridQueryPayload = React.useMemo(
    () =>
      buildSnapshotGridQueryPayload({
        offset: page - 1,
        limit: PAGE_SIZE,
        query,
        ordering,
        subcollectionId,
        contractFamilyId,
        supplierContractFolderId,
        columnFilters,
        dateFilter,
        fields: gridMetadata?.fields ?? [],
      }),
    [
      page,
      query,
      ordering,
      subcollectionId,
      contractFamilyId,
      supplierContractFolderId,
      columnFilters,
      dateFilter,
      gridMetadata?.fields,
    ],
  );

  const { configuration: effectiveViewConfiguration, dirty: isViewDirty } = useSnapshotGridConfiguration({
    initialConfiguration,
    fields: gridMetadata?.fields ?? [],
    hiddenColumns,
    columnOrder,
    noteColumnOrder,
    columnFilters,
    dateFilter,
    ordering,
    dateRangeTouched,
    stableColumnOrder,
  });

  const currentGridViewKey = React.useMemo(
    () => getSnapshotGridViewKey(collectionId, gridQueryPayload),
    [collectionId, gridQueryPayload],
  );

  const gridDataSource = useSnapshotGridDataSource({
    mode,
    snapshotCollectionId: collectionId ?? "",
    snapshotViewId,
    snapshotViewConfiguration: initialConfigurationAppliedRef.current
      ? effectiveViewConfiguration
      : initialConfiguration,
    payload: gridQueryPayload,
    capabilities,
  });
  const { query: activeGridQuery, page: dataSourcePage, capabilities: gridCapabilities } = gridDataSource;
  const fetchRowByResult = useSnapshotGridRowFetch(collectionId, isContractFamilyProjection);
  // A cell edit refreshes just its row (see `api/grid.ts`); rows fetched that way replace the loaded copy
  // on whichever page it sits. Rows already in the cache when this grid mounted are not replayed.
  const refreshedRows = useRefreshedSnapshotGridRows(collectionId, isContractFamilyProjection);
  const appliedRefreshedRows = React.useRef(new WeakSet<BusinessReportOutcome>());
  React.useEffect(() => {
    const fresh = refreshedRows.filter(row => !appliedRefreshedRows.current.has(row));
    if (fresh.length === 0) return;
    for (const row of fresh) appliedRefreshedRows.current.add(row);
    setLoadedGridRows(previousRows => replaceSnapshotGridRows(previousRows, fresh.map(adaptSnapshotOutcomeRow)));
  }, [refreshedRows]);
  const { isLoading, refetch, isError, isFetching } = activeGridQuery;
  const refreshVisibleGrid = React.useCallback(() => {
    if (page === 1) {
      void refetch();
    } else {
      setPage(1);
    }
  }, [page, refetch, setPage]);
  useSnapshotCollectionUpdates(collectionId, refreshVisibleGrid);
  const previousRefreshSignal = React.useRef(refreshSignal);
  React.useEffect(() => {
    if (!shouldRefetchSnapshotGrid(previousRefreshSignal.current, refreshSignal)) return;
    previousRefreshSignal.current = refreshSignal;
    void activeGridQuery.refetch();
  }, [activeGridQuery, refreshSignal]);
  React.useEffect(() => {
    onQueryChange?.(gridQueryPayload);
  }, [gridQueryPayload, onQueryChange]);
  const effectiveGridMetadata = gridMetadata ?? dataSourcePage ?? null;
  const hasRenderableGrid = Boolean(effectiveGridMetadata || loadedGridRows.length > 0);

  React.useEffect(() => {
    if (!dataSourcePage) return;

    setGridMetadata(dataSourcePage);
    if (dataSourcePage.pagination.offset === 0) {
      setAppliedGridViewKey(currentGridViewKey);
    }
    isGridPageRequestPendingRef.current = false;
    setLoadedGridRows(previousRows =>
      dataSourcePage.pagination.offset === 0
        ? dataSourcePage.rows
        : mergeSnapshotGridRows(previousRows, dataSourcePage.rows),
    );
  }, [currentGridViewKey, dataSourcePage, isGridPageRequestPendingRef]);

  const totalGridRows = effectiveGridMetadata?.pagination.total ?? 0;
  const isGridResultStale = Boolean(
    hasRenderableGrid && appliedGridViewKey && appliedGridViewKey !== currentGridViewKey,
  );
  const isInitialGridLoading = isLoading && !hasRenderableGrid;
  const isLoadingMoreGridRows = isFetching && gridQueryPayload.offset > 0 && loadedGridRows.length > 0;
  const hasNextGridPage = getHasNextSnapshotGridPage({
    loadedCount: Math.min(page * PAGE_SIZE, totalGridRows),
    total: totalGridRows,
  });
  const requestNextGridPage = React.useCallback(() => {
    if (!hasNextGridPage || isFetching || isGridPageRequestPendingRef.current) return;
    isGridPageRequestPendingRef.current = true;
    setPage(previousPage => previousPage + 1);
  }, [hasNextGridPage, isFetching, isGridPageRequestPendingRef, setPage]);

  return {
    gridQueryPayload,
    effectiveViewConfiguration,
    isViewDirty,
    gridMetadata,
    effectiveGridMetadata,
    gridCapabilities,
    fetchRowByResult,
    refetch,
    isError,
    isFetching,
    loadedGridRows,
    hasRenderableGrid,
    totalGridRows,
    isGridResultStale,
    isInitialGridLoading,
    isLoadingMoreGridRows,
    hasNextGridPage,
    requestNextGridPage,
  };
};
